/**
 * SOPs Overview Page
 * 
 * World-class UI/UX for displaying Standard Operating Procedures with:
 * - Tabs: By Stage / By Role / By Scenario
 * - Global search and filters (brand, type, priority, stage, role)
 * - SOP cards with derived stage/role names
 * - EN/AR bilingual support with RTL
 * 
 * All data is driven by the consolidated Markdown registry.
 */

import React, { useState, useMemo } from 'react';
import { usePrint } from '../../hooks/usePrint';
import PrintButton from '../../components/PrintButton';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  FileText, 
  Search,
  Layers,
  Users,
  AlertTriangle,
  Clock,
  CheckCircle,
  Target,
  AlertCircle,
  Book,
  Zap,
  UserCheck,
  ArrowRight
} from 'lucide-react';
import { BrandSelector } from '../../components/pipeline/BrandSelector';
import {
  getSOPRegistry,
  getAllSOPs,
  getStageSOPs,
  getScenarioSOPs,
  getSOPCounts,
  getSOPsForRole,
} from '../../lib/sopsRegistry';
import { getAllStages, getStageById } from '../../lib/leadPipelineRegistry';
import { getAllRoles, getRoleById, getRoleGroups } from '../../lib/rolesRegistry';
import type { 
  PipelineBrand, 
  SOPPriority,
  SOPRegistryEntry,
  SOPType,
} from '../../types';

// =============================================================================
// Sub-Components
// =============================================================================

interface StatCardProps {
  label: string;
  value: number | string;
  icon: React.ReactNode;
  color: 'blue' | 'green' | 'purple' | 'amber' | 'red' | 'cyan' | 'orange';
}

const StatCard: React.FC<StatCardProps> = ({ label, value, icon, color }) => {
  const colorClasses = {
    blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800',
    green: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800',
    purple: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 border-purple-200 dark:border-purple-800',
    amber: 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-800',
    red: 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800',
    cyan: 'bg-cyan-50 dark:bg-cyan-900/20 text-cyan-600 dark:text-cyan-400 border-cyan-200 dark:border-cyan-800',
    orange: 'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 border-orange-200 dark:border-orange-800',
  };

  return (
    <div className={`rounded-xl p-4 border ${colorClasses[color]} transition-all hover:shadow-md`}>
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-white/80 dark:bg-gray-800/80">
          {icon}
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
          <p className="text-sm opacity-80">{label}</p>
        </div>
      </div>
    </div>
  );
};

// Priority Badge Component
const PriorityBadge: React.FC<{ priority: SOPPriority; isArabic: boolean; size?: 'sm' | 'md' }> = ({ 
  priority, 
  isArabic,
  size = 'sm'
}) => {
  const config = {
    critical: { 
      bg: 'bg-red-100 dark:bg-red-900/40', 
      text: 'text-red-700 dark:text-red-300',
      border: 'border-red-200 dark:border-red-800',
      label: isArabic ? 'حرج' : 'Critical',
      icon: <AlertCircle className="w-3.5 h-3.5" />
    },
    high: { 
      bg: 'bg-orange-100 dark:bg-orange-900/40', 
      text: 'text-orange-700 dark:text-orange-300',
      border: 'border-orange-200 dark:border-orange-800',
      label: isArabic ? 'عالي' : 'High',
      icon: <AlertTriangle className="w-3.5 h-3.5" />
    },
    medium: { 
      bg: 'bg-yellow-100 dark:bg-yellow-900/40', 
      text: 'text-yellow-700 dark:text-yellow-300',
      border: 'border-yellow-200 dark:border-yellow-800',
      label: isArabic ? 'متوسط' : 'Medium',
      icon: <Target className="w-3.5 h-3.5" />
    },
    low: { 
      bg: 'bg-green-100 dark:bg-green-900/40', 
      text: 'text-green-700 dark:text-green-300',
      border: 'border-green-200 dark:border-green-800',
      label: isArabic ? 'منخفض' : 'Low',
      icon: <CheckCircle className="w-3.5 h-3.5" />
    },
  };
  
  const c = config[priority];
  const sizeClasses = size === 'md' ? 'px-3 py-1.5 text-sm' : 'px-2.5 py-1 text-xs';
  
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border font-medium ${sizeClasses} ${c.bg} ${c.text} ${c.border}`}>
      {c.icon}
      {c.label}
    </span>
  );
};

// SOP Type Badge
const SOPTypeBadge: React.FC<{ type: SOPType; isArabic: boolean }> = ({ type, isArabic }) => {
  const config = {
    stage: {
      bg: 'bg-blue-100 dark:bg-blue-900/30',
      text: 'text-blue-700 dark:text-blue-300',
      icon: <Layers className="w-3.5 h-3.5" />,
      label: isArabic ? 'مرحلة' : 'Stage'
    },
    scenario: {
      bg: 'bg-purple-100 dark:bg-purple-900/30',
      text: 'text-purple-700 dark:text-purple-300',
      icon: <AlertTriangle className="w-3.5 h-3.5" />,
      label: isArabic ? 'سيناريو' : 'Scenario'
    }
  };
  
  const c = config[type];
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
      {c.icon}
      {c.label}
    </span>
  );
};

// SOP Card Component
const SOPCard: React.FC<{ sop: SOPRegistryEntry; isArabic: boolean }> = ({ sop, isArabic }) => {
  // Derive stage and role names
  const stage = sop.meta.stageId ? getStageById(sop.meta.stageId) : null;
  const primaryRole = sop.meta.primaryOwnerRoleId ? getRoleById(sop.meta.primaryOwnerRoleId) : null;
  const roleGroups = getRoleGroups();
  const roleGroup = primaryRole ? roleGroups.find(g => g.id === primaryRole.group) : null;

  // Stage colors for visual distinction
  const stageColors = [
    'from-blue-500 to-cyan-500',
    'from-indigo-500 to-violet-500',
    'from-purple-500 to-pink-500',
    'from-rose-500 to-orange-500',
    'from-amber-500 to-yellow-500',
    'from-lime-500 to-green-500',
    'from-emerald-500 to-teal-500',
    'from-cyan-500 to-blue-500',
    'from-blue-500 to-indigo-500',
    'from-violet-500 to-purple-500',
  ];
  
  const getGradient = () => {
    if (sop.meta.type === 'scenario') {
      return 'from-purple-500 to-indigo-600';
    }
    if (stage) {
      const stageOrder = stage.meta.order;
      return stageColors[(stageOrder - 1) % stageColors.length];
    }
    return 'from-gray-500 to-slate-500';
  };

  return (
    <Link
      to={`/sops/${sop.meta.id}`}
      className="group bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
    >
      {/* Card Header with Gradient */}
      <div className={`bg-gradient-to-br ${getGradient()} p-5 text-white`}>
        <div className="flex items-start justify-between gap-3">
          <div className="p-2.5 bg-white/20 rounded-xl backdrop-blur-sm">
            {sop.meta.type === 'stage' ? (
              <Layers className="w-5 h-5" />
            ) : (
              <AlertTriangle className="w-5 h-5" />
            )}
          </div>
          <PriorityBadge priority={sop.meta.priority} isArabic={isArabic} />
        </div>
        <h4 className="text-lg font-bold mt-3 leading-snug line-clamp-2">
          {isArabic ? sop.meta.titleAr : sop.meta.title}
        </h4>
        <p className="text-white/80 text-sm mt-1 line-clamp-2">
          {isArabic ? sop.meta.shortDescAr : sop.meta.shortDesc}
        </p>
      </div>
      
      {/* Card Body */}
      <div className="p-5 space-y-4">
        {/* Stage & Role Info */}
        <div className="flex flex-wrap gap-2">
          <SOPTypeBadge type={sop.meta.type} isArabic={isArabic} />
          
          {stage && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
              <Zap className="w-3.5 h-3.5" />
              {isArabic ? stage.meta.titleAr : stage.meta.title}
            </span>
          )}
        </div>
        
        {/* Primary Owner */}
        {primaryRole && (
          <div className="flex items-center gap-2 py-2 px-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
            <div 
              className="p-1.5 rounded-lg"
              style={{ backgroundColor: `${roleGroup?.color || '#10b981'}20` }}
            >
              <UserCheck 
                className="w-4 h-4"
                style={{ color: roleGroup?.color || '#10b981' }}
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {isArabic ? 'المسؤول الرئيسي' : 'Primary Owner'}
              </p>
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {isArabic ? primaryRole.titleAr : primaryRole.title}
              </p>
            </div>
          </div>
        )}

        {/* Triggers Preview */}
        {sop.meta.triggers.length > 0 && (
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-1">
              <Zap className="w-3.5 h-3.5" />
              {isArabic ? 'المحفزات' : 'Triggers'}
            </p>
            <div className="space-y-1">
              {sop.meta.triggers.slice(0, 2).map((trigger, idx) => (
                <p key={idx} className="text-xs text-gray-600 dark:text-gray-400 truncate">
                  • {trigger}
                </p>
              ))}
              {sop.meta.triggers.length > 2 && (
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  +{sop.meta.triggers.length - 2} {isArabic ? 'أخرى' : 'more'}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Related SLAs Count */}
        {sop.meta.relatedSlaIds.length > 0 && (
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <Clock className="w-3.5 h-3.5" />
            <span>
              {sop.meta.relatedSlaIds.length} {isArabic ? 'اتفاقيات مستوى الخدمة مرتبطة' : 'Related SLAs'}
            </span>
          </div>
        )}

        {/* View Details Link */}
        <div className="flex items-center justify-between pt-2 mt-2 border-t border-gray-100 dark:border-gray-700">
          <span className="text-xs text-gray-400">
            {isArabic ? 'آخر تحديث: ' : 'Updated: '}{sop.meta.lastUpdated}
          </span>
          <span className="flex items-center gap-1 text-sm font-medium text-indigo-600 dark:text-indigo-400 group-hover:text-indigo-700 dark:group-hover:text-indigo-300 transition-colors">
            {isArabic ? 'عرض' : 'View'}
            <ArrowRight className={`w-4 h-4 transition-transform group-hover:translate-x-1 ${isArabic ? 'rotate-180 group-hover:-translate-x-1' : ''}`} />
          </span>
        </div>
      </div>
    </Link>
  );
};

// Empty State Component
const EmptyState: React.FC<{ isArabic: boolean; type: string }> = ({ isArabic, type }) => (
  <div className="col-span-full text-center py-16">
    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
      <FileText className="w-8 h-8 text-gray-400" />
    </div>
    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
      {isArabic ? 'لم يتم العثور على إجراءات' : 'No SOPs Found'}
    </h3>
    <p className="text-gray-500 dark:text-gray-400">
      {isArabic 
        ? `لا توجد إجراءات ${type} تطابق معايير البحث الحالية` 
        : `No ${type} SOPs match your current search criteria`
      }
    </p>
  </div>
);

// =============================================================================
// Main Component
// =============================================================================

const SOPsOverview: React.FC = () => {
  const { i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';
  const { printRef, handlePrint } = usePrint('Almajdouie Motors – Standard Operating Procedures');
  
  // State
  const [selectedBrand, setSelectedBrand] = useState<PipelineBrand>('Changan');
  const [activeTab, setActiveTab] = useState<string>('stage');
  const [searchTerm, setSearchTerm] = useState('');
  const [priorityFilter, setPriorityFilter] = useState<SOPPriority | 'all'>('all');
  const [stageFilter, setStageFilter] = useState<string>('all');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  
  // Get data from registry
  const registry = getSOPRegistry();
  const counts = getSOPCounts();
  const stages = getAllStages();
  const roles = getAllRoles();
  
  // Filter SOPs based on current tab and filters
  const filteredSOPs = useMemo(() => {
    let sops: SOPRegistryEntry[];
    
    // Filter by tab (type)
    if (activeTab === 'stage') {
      sops = getStageSOPs();
    } else if (activeTab === 'scenario') {
      sops = getScenarioSOPs();
    } else if (activeTab === 'role') {
      sops = roleFilter !== 'all' ? getSOPsForRole(roleFilter) : getAllSOPs();
    } else {
      sops = getAllSOPs();
    }
    
    // Apply brand filter
    sops = sops.filter(sop => sop.meta.appliesToBrands.includes(selectedBrand));
    
    // Apply stage filter (only for stage tab or if explicitly selected)
    if (stageFilter !== 'all') {
      sops = sops.filter(sop => sop.meta.stageId === stageFilter);
    }
    
    // Apply role filter (for role tab)
    if (activeTab === 'role' && roleFilter !== 'all') {
      sops = sops.filter(sop => 
        sop.meta.primaryOwnerRoleId === roleFilter ||
        sop.meta.supportingRoleIds.includes(roleFilter)
      );
    }
    
    // Apply priority filter
    if (priorityFilter !== 'all') {
      sops = sops.filter(sop => sop.meta.priority === priorityFilter);
    }
    
    // Apply search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      sops = sops.filter(sop => 
        sop.meta.title.toLowerCase().includes(searchLower) ||
        sop.meta.titleAr.includes(searchTerm) ||
        sop.meta.shortDesc.toLowerCase().includes(searchLower) ||
        sop.meta.id.toLowerCase().includes(searchLower)
      );
    }
    
    return sops;
  }, [activeTab, selectedBrand, stageFilter, roleFilter, priorityFilter, searchTerm]);
  
  // Tab configuration
  const tabs = [
    { id: 'stage', label: isArabic ? 'حسب المرحلة' : 'By Stage', icon: Layers, count: counts.stageSOPs },
    { id: 'role', label: isArabic ? 'حسب الدور' : 'By Role', icon: Users, count: counts.totalSOPs },
    { id: 'scenario', label: isArabic ? 'السيناريوهات' : 'Scenarios', icon: AlertTriangle, count: counts.scenarioSOPs },
  ];

  // Group SOPs by stage for stage tab
  const sopsByStage = useMemo(() => {
    if (activeTab !== 'stage') return null;
    
    const grouped = new Map<string, { stage: typeof stages[0] | null | undefined; sops: SOPRegistryEntry[] }>();
    
    filteredSOPs.forEach(sop => {
      const stageId = sop.meta.stageId || 'no-stage';
      const stage = sop.meta.stageId ? getStageById(sop.meta.stageId) : null;
      
      if (!grouped.has(stageId)) {
        grouped.set(stageId, { stage, sops: [] });
      }
      grouped.get(stageId)?.sops.push(sop);
    });
    
    // Sort by stage order
    return Array.from(grouped.entries())
      .sort((a, b) => {
        const orderA = a[1].stage?.meta.order ?? 999;
        const orderB = b[1].stage?.meta.order ?? 999;
        return orderA - orderB;
      });
  }, [activeTab, filteredSOPs]);

  // Group SOPs by role for role tab
  const sopsByRole = useMemo(() => {
    if (activeTab !== 'role') return null;
    
    const grouped = new Map<string, { role: ReturnType<typeof getRoleById> | undefined; sops: SOPRegistryEntry[] }>();
    
    filteredSOPs.forEach(sop => {
      const roleId = sop.meta.primaryOwnerRoleId || 'no-role';
      const role = getRoleById(roleId);
      
      if (!grouped.has(roleId)) {
        grouped.set(roleId, { role: role ?? undefined, sops: [] });
      }
      grouped.get(roleId)?.sops.push(sop);
    });
    
    return Array.from(grouped.entries());
  }, [activeTab, filteredSOPs]);

  return (
    <div ref={printRef} className="space-y-8">
      {/* Hero Header */}
      <div className="bg-gradient-to-br from-amber-600 via-orange-600 to-red-600 rounded-2xl p-8 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/10 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))]" />
        <div className="relative z-10">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                  <Book className="w-6 h-6" />
                </div>
                <span className="text-white/80 text-sm font-medium uppercase tracking-wider">
                  {isArabic ? 'إجراءات التشغيل' : 'Standard Operating Procedures'}
                </span>
              </div>
              <h1 className="text-4xl font-bold mb-3">
                {isArabic ? 'إجراءات التشغيل الموحدة' : 'SOPs'}
              </h1>
              <p className="text-white/80 max-w-2xl text-lg">
                {isArabic 
                  ? registry.purposeAr || 'إجراءات التشغيل الموحدة لجميع مراحل المسار والسيناريوهات'
                  : registry.purpose || 'Standard Operating Procedures for all pipeline stages and scenarios'
                }
              </p>
            </div>
            
            {/* Brand Selector + Print */}
            <div className="flex-shrink-0 flex flex-col gap-3 items-end">
              <PrintButton
                onClick={handlePrint}
                isAr={isArabic}
                className="bg-white/10 border-white/30 text-white hover:bg-white/20"
              />
              <BrandSelector 
                selectedBrand={selectedBrand} 
                onBrandChange={setSelectedBrand}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Summary Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <StatCard 
          label={isArabic ? 'إجمالي الإجراءات' : 'Total SOPs'}
          value={counts.totalSOPs}
          icon={<FileText className="w-5 h-5" />}
          color="blue"
        />
        <StatCard 
          label={isArabic ? 'إجراءات المراحل' : 'Stage SOPs'}
          value={counts.stageSOPs}
          icon={<Layers className="w-5 h-5" />}
          color="green"
        />
        <StatCard 
          label={isArabic ? 'إجراءات السيناريوهات' : 'Scenario SOPs'}
          value={counts.scenarioSOPs}
          icon={<AlertTriangle className="w-5 h-5" />}
          color="purple"
        />
        {counts.byPriority.filter(p => p.priority === 'critical' || p.priority === 'high').map((p) => (
          <StatCard 
            key={p.priority}
            label={isArabic 
              ? (p.priority === 'critical' ? 'حرج' : 'عالي الأولوية')
              : (p.priority === 'critical' ? 'Critical' : 'High Priority')
            }
            value={p.count}
            icon={p.priority === 'critical' ? <AlertCircle className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
            color={p.priority === 'critical' ? 'red' : 'orange'}
          />
        ))}
      </div>

      {/* Search and Filter Bar */}
      <div className="flex flex-col lg:flex-row gap-4 bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder={isArabic ? 'البحث في الإجراءات...' : 'Search SOPs...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
          />
        </div>
        
        {/* Priority Filter */}
        <select
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value as SOPPriority | 'all')}
          className="px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent min-w-[160px]"
        >
          <option value="all">{isArabic ? 'جميع الأولويات' : 'All Priorities'}</option>
          <option value="critical">{isArabic ? 'حرج' : 'Critical'}</option>
          <option value="high">{isArabic ? 'عالي' : 'High'}</option>
          <option value="medium">{isArabic ? 'متوسط' : 'Medium'}</option>
          <option value="low">{isArabic ? 'منخفض' : 'Low'}</option>
        </select>
        
        {/* Stage Filter (for stage tab) */}
        {activeTab === 'stage' && (
          <select
            value={stageFilter}
            onChange={(e) => setStageFilter(e.target.value)}
            className="px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent min-w-[200px]"
          >
            <option value="all">{isArabic ? 'جميع المراحل' : 'All Stages'}</option>
            {stages.map(stage => (
              <option key={stage.meta.id} value={stage.meta.id}>
                {stage.meta.order}. {isArabic ? stage.meta.titleAr : stage.meta.title}
              </option>
            ))}
          </select>
        )}
        
        {/* Role Filter (for role tab) */}
        {activeTab === 'role' && (
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent min-w-[200px]"
          >
            <option value="all">{isArabic ? 'جميع الأدوار' : 'All Roles'}</option>
            {roles.map(role => (
              <option key={role.id} value={role.id}>
                {isArabic ? role.titleAr : role.title}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex space-x-8 overflow-x-auto" aria-label="Tabs">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  // Reset relevant filters when switching tabs
                  if (tab.id !== 'stage') setStageFilter('all');
                  if (tab.id !== 'role') setRoleFilter('all');
                }}
                className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'border-amber-500 text-amber-600 dark:text-amber-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                <Icon className="w-5 h-5" />
                {tab.label}
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  activeTab === tab.id 
                    ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400' 
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                }`}>
                  {tab.count}
                </span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="min-h-[400px]">
        {/* Stage Tab - Grouped by Stage */}
        {activeTab === 'stage' && sopsByStage && (
          <div className="space-y-8">
            {sopsByStage.length === 0 ? (
              <EmptyState isArabic={isArabic} type="stage" />
            ) : (
              sopsByStage.map(([stageId, { stage, sops }]) => (
                <div key={stageId} className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                  {/* Stage Header Card */}
                  <div 
                    className="p-5 border-b border-gray-100 dark:border-gray-700"
                    style={{ 
                      background: stage 
                        ? `linear-gradient(135deg, ${stage.meta.color || '#6366f1'}15 0%, transparent 100%)`
                        : undefined 
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {/* Stage Number Badge */}
                        {stage && (
                          <div 
                            className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-lg"
                            style={{ backgroundColor: stage.meta.color || '#6366f1' }}
                          >
                            {stage.meta.order}
                          </div>
                        )}
                        {!stage && (
                          <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-gray-400 text-white font-bold text-xl shadow-lg">
                            <AlertTriangle className="w-6 h-6" />
                          </div>
                        )}
                        
                        {/* Stage Info */}
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                              {stage 
                                ? (isArabic ? stage.meta.titleAr : stage.meta.title)
                                : (isArabic ? 'بدون مرحلة' : 'No Stage')
                              }
                            </h3>
                          </div>
                          
                          {/* Stage Type Badge & SOP Count */}
                          <div className="flex items-center gap-3">
                            {stage && (
                              <span 
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                                style={{ 
                                  backgroundColor: `${stage.meta.color || '#6366f1'}20`,
                                  color: stage.meta.color || '#6366f1'
                                }}
                              >
                                <Layers className="w-3.5 h-3.5" />
                                {isArabic ? 'مرحلة CRM' : 'CRM Stage'}
                              </span>
                            )}
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              {sops.length} {isArabic ? 'إجراء تشغيلي' : 'SOP'}{sops.length !== 1 && !isArabic ? 's' : ''}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* View Stage Link */}
                      {stage && (
                        <Link 
                          to={`/pipeline/stage/${stage.meta.id}`}
                          className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl transition-all"
                          style={{ 
                            backgroundColor: `${stage.meta.color || '#6366f1'}15`,
                            color: stage.meta.color || '#6366f1'
                          }}
                        >
                          {isArabic ? 'عرض المرحلة' : 'View Stage'}
                          <ArrowRight className={`w-4 h-4 ${isArabic ? 'rotate-180' : ''}`} />
                        </Link>
                      )}
                    </div>
                  </div>
                  
                  {/* SOPs Grid */}
                  <div className="p-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                      {sops.map(sop => (
                        <SOPCard key={sop.meta.id} sop={sop} isArabic={isArabic} />
                      ))}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Role Tab - Grouped by Role */}
        {activeTab === 'role' && sopsByRole && (
          <div className="space-y-8">
            {sopsByRole.length === 0 ? (
              <EmptyState isArabic={isArabic} type="role" />
            ) : (
              sopsByRole.map(([roleId, { role, sops }]) => {
                const roleGroups = getRoleGroups();
                const roleGroup = role ? roleGroups.find(g => g.id === role.group) : null;
                const roleColor = role?.color || roleGroup?.color || '#10b981';
                
                return (
                  <div key={roleId} className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                    {/* Role Header Card */}
                    <div 
                      className="p-5 border-b border-gray-100 dark:border-gray-700"
                      style={{ 
                        background: `linear-gradient(135deg, ${roleColor}15 0%, transparent 100%)`
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          {/* Role Icon */}
                          <div 
                            className="w-12 h-12 rounded-xl flex items-center justify-center"
                            style={{ backgroundColor: `${roleColor}20` }}
                          >
                            <UserCheck 
                              className="w-6 h-6"
                              style={{ color: roleColor }}
                            />
                          </div>
                          
                          {/* Role Info */}
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                                {role 
                                  ? (isArabic ? role.titleAr : role.title)
                                  : (isArabic ? 'بدون دور' : 'No Role')
                                }
                              </h3>
                            </div>
                            
                            {/* Role Group Badge & SOP Count */}
                            <div className="flex items-center gap-3">
                              {roleGroup && (
                                <span 
                                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                                  style={{ 
                                    backgroundColor: `${roleColor}20`,
                                    color: roleColor
                                  }}
                                >
                                  <Users className="w-3.5 h-3.5" />
                                  {isArabic ? roleGroup.labelAr : roleGroup.label}
                                </span>
                              )}
                              <span className="text-sm text-gray-500 dark:text-gray-400">
                                {sops.length} {isArabic ? 'إجراء تشغيلي' : 'SOP'}{sops.length !== 1 && !isArabic ? 's' : ''}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        {/* View Role Link */}
                        {role && (
                          <Link 
                            to={`/roles/${role.id}`}
                            className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl transition-all"
                            style={{ 
                              backgroundColor: `${roleColor}15`,
                              color: roleColor
                            }}
                          >
                            {isArabic ? 'عرض الدور' : 'View Role'}
                            <ArrowRight className={`w-4 h-4 ${isArabic ? 'rotate-180' : ''}`} />
                          </Link>
                        )}
                      </div>
                    </div>
                    
                    {/* SOPs Grid */}
                    <div className="p-5">
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                        {sops.map(sop => (
                          <SOPCard key={sop.meta.id} sop={sop} isArabic={isArabic} />
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Scenario Tab - Simple Grid */}
        {activeTab === 'scenario' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredSOPs.length === 0 ? (
              <EmptyState isArabic={isArabic} type="scenario" />
            ) : (
              filteredSOPs.map(sop => (
                <SOPCard key={sop.meta.id} sop={sop} isArabic={isArabic} />
              ))
            )}
          </div>
        )}
      </div>

      {/* Version Info */}
      <div className="text-center text-sm text-gray-500 dark:text-gray-400">
        {isArabic ? 'الإصدار' : 'Version'} {registry.version} | 
        {isArabic ? ' آخر تحديث: ' : ' Last Updated: '}{registry.lastUpdated}
      </div>
    </div>
  );
};

export default SOPsOverview;
